// src/pages/Challenges.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Sparkles, Users, Target, Swords, Info } from "lucide-react";

const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

/* ---------- Aurora theme ---------- */
const AURORA_BG = {
    backgroundColor: "#0b1220",
    backgroundImage: `
    radial-gradient(1200px 800px at -20% -10%, rgba(14,165,233,.25), transparent 60%),
    radial-gradient(1200px 800px at 120% 0%, rgba(34,211,238,.22), transparent 55%),
    radial-gradient(1000px 800px at 110% 120%, rgba(168,85,247,.18), transparent 55%),
    radial-gradient(800px 600px at 0% 120%, rgba(16,185,129,.16), transparent 55%)
  `,
};
const CARD = "rounded-2xl border border-white/10 bg-white/[.05] backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,.25)]";
const norm = (s) => (s ?? "").toString().toLowerCase();

/* ---------- tiny static fallback (used sparingly) ---------- */
const STATIC_REGION_FALLBACK = {
    "Void": ["Cho'Gath","Kha'Zix","Kog'Maw","Kassadin","Malzahar","Vel'Koz","Rek'Sai","Kai'Sa","Bel'Veth"],
    "Bandle City": ["Teemo","Tristana","Lulu","Poppy","Veigar","Heimerdinger","Kennen","Rumble","Gnar","Corki","Kled"],
    "Demacia": ["Garen","Lux","Fiora","Jarvan IV","Quinn","Shyvana","Xin Zhao","Vayne","Poppy","Sona","Galio","Sylas"],
    "Noxus": ["Darius","Draven","Katarina","LeBlanc","Swain","Cassiopeia","Riven","Sion","Samira","Kled"],
    "Ionia": ["Irelia","Yasuo","Yone","Ahri","Karma","Jhin","Shen","Akali","Kennen","Syndra","Zed","Rakan","Xayah","Wukong"],
    "Freljord": ["Ashe","Sejuani","Lissandra","Trundle","Anivia","Braum","Olaf","Tryndamere","Nunu & Willump","Udyr","Ornn","Volibear","Gnar","Gragas"],
    "Shurima": ["Azir","Nasus","Renekton","Sivir","Xerath","Taliyah","Amumu","Rammus","K’Sante","Naafiri","Skarner"],
    "Piltover": ["Caitlyn","Jayce","Heimerdinger","Orianna","Vi","Ezreal"],
    "Zaun": ["Jinx","Ziggs","Ekko","Twitch","Warwick","Singed","Viktor","Blitzcrank","Dr. Mundo"],
    "Shadow Isles": ["Thresh","Hecarim","Kalista","Elise","Yorick","Gwen","Viego","Maokai"],
    "Targon": ["Leona","Diana","Taric","Pantheon","Zoe","Aurelion Sol","Soraka","Aphelios"],
    "Bilgewater": ["Miss Fortune","Gangplank","Nautilus","Pyke","Illaoi","Fizz","Graves","Twisted Fate","Tahm Kench"],
    "Ixtal": ["Qiyana","Neeko","Nidalee","Zyra"],
};

/* ---------- lightweight anchors to guarantee iconic champs per region ---------- */
const REGION_ANCHORS = {
    "Freljord": ["Ashe","Sejuani","Lissandra","Nunu & Willump","Gnar"],
    "Demacia": ["Garen","Lux","Jarvan IV"],
    "Noxus": ["Darius","Swain","Katarina"],
    "Ionia": ["Irelia","Ahri","Yasuo"],
    "Shurima": ["Azir","Nasus","Renekton"],
    "Piltover": ["Caitlyn","Vi","Jayce"],
    "Zaun": ["Jinx","Ekko","Singed"],
    "Shadow Isles": ["Thresh","Hecarim","Viego"],
    "Targon": ["Leona","Diana","Pantheon"],
    "Bilgewater": ["Miss Fortune","Gangplank","Illaoi"],
    "Ixtal": ["Qiyana","Neeko","Nidalee"],
    "Bandle City": ["Teemo","Tristana","Lulu"],
    "Void": ["Kassadin","Kha'Zix","Cho'Gath"]
};

/* ---------- Role inference helpers ---------- */
const LANE_OVERRIDES = {
    "Nunu & Willump": ["jg"],
    "Rammus": ["jg"], "Sejuani": ["jg"], "Shyvana": ["jg"], "Udyr": ["jg"],
    "Nocturne": ["jg"], "Fiddlesticks": ["jg"], "Kayn": ["jg"], "Lee Sin": ["jg"],
    "Kindred": ["jg"], "Graves": ["jg"], "Hecarim": ["jg"], "Kha'Zix": ["jg"], "Elise": ["jg"], "Evelynn": ["jg"], "Zac": ["jg"],
    "Garen": ["top"], "Darius": ["top"], "Riven": ["top"], "Tryndamere": ["top"], "Sett": ["top"], "Illaoi": ["top"], "Ornn": ["top"], "Cho'Gath": ["top"],
    "Yasuo": ["mid","top"], "Yone": ["mid","top"], "Sylas": ["mid","top"], "Kassadin": ["mid"],
    "Kai'Sa": ["adc"], "Kog'Maw": ["adc"], "Samira": ["adc"], "Jinx": ["adc"], "Tristana": ["adc"], "Sivir": ["adc"], "Ezreal": ["adc"], "Caitlyn": ["adc"], "Aphelios": ["adc"], "Miss Fortune": ["adc"],
    "Leona": ["sup"], "Nautilus": ["sup"], "Thresh": ["sup"], "Braum": ["sup"], "Taric": ["sup"], "Soraka": ["sup"], "Sona": ["sup"], "Rakan": ["sup"], "Maokai": ["sup","top","jg"],
    "Vel'Koz": ["mid","sup"], "Xerath": ["mid","sup"], "Brand": ["mid","sup"], "Zyra": ["mid","sup"], "Swain": ["mid","sup"], "Lux": ["mid","sup"], "Morgana": ["mid","sup"],
};

function scoreRoles(ch) {
    const tags = ch.tags || [];
    const range = ch.range ?? 125;
    const pt = ch.partype || "mana";
    const s = { top:0, jg:0, mid:0, adc:0, sup:0 };

    if (tags.includes("marksman")) { s.adc += 3; s.mid += 0.4; s.sup += 0.2; s.top -= 0.5; s.jg -= 0.6; }
    if (tags.includes("mage"))     { s.mid += 2; s.sup += 0.8; }
    if (tags.includes("assassin")) { s.mid += 1.8; s.jg += 1.2; s.top += 0.3; }
    if (tags.includes("fighter"))  { s.top += 1.7; s.jg += 1.1; s.mid += 0.3; }
    if (tags.includes("tank"))     { s.top += 1.9; s.sup += 1.1; s.jg += 0.7; }
    if (tags.includes("support"))  { s.sup += 3; s.mid += 0.2; }

    if (range >= 525) { s.adc += 1.1; s.mid += 0.5; s.sup += 0.4; s.top -= 0.4; s.jg -= 0.7; }
    else if (range <= 175) { s.top += 0.5; s.jg += 0.4; s.adc -= 0.8; }

    if (pt === "energy") { s.mid += 0.3; s.top += 0.2; }

    const ov = LANE_OVERRIDES[ch.name];
    if (ov) ov.forEach((lane, i) => s[lane] += (i === 0 ? 3 : 1));

    return Object.entries(s).sort((a,b)=>b[1]-a[1]).map(([lane])=>lane);
}

/* ---------- Region synonyms (richer coverage) ---------- */
const REGION_SYNONYMS = {
    "Void": ["void","voidborn","from the void","icathia"],
    "Bandle City": ["bandle","yordle","bandle city"],
    "Demacia": ["demacia","demacian","petricite","mageseeker","crownsguard","crown guard"],
    "Noxus": ["noxus","noxian","trifarian"],
    "Ionia": ["ionia","ionian","vastaya","kinkou"],
    "Freljord": ["freljord","freljordian","avarosan","winter's claw","winters claw","frostguard","iceborn","yeti","poro","lokfar","notai"],
    "Shurima": ["shurima","shuriman","sun disc","sun-disc","emperor of the sands"],
    "Piltover": ["piltover","piltovan","hextech","enforcer","progress day"],
    "Zaun": ["zaun","zaunite","chemtech","sump","sump-scraper","the gray"],
    "Shadow Isles": ["shadow isles","ruination","black mist","camavor","ruined king","harrowing"],
    "Targon": ["targon","mount targon","targonian","aspect","solari","lunari"],
    "Bilgewater": ["bilgewater","pirate","port city","serpent isles","slaughter docks"],
    "Ixtal": ["ixtal","yun tal"]
};

function guessRegionsFromText(text) {
    const t = (text || "").toLowerCase();
    const hits = new Set();
    for (const [region, keys] of Object.entries(REGION_SYNONYMS)) {
        if (keys.some(k => t.includes(k))) hits.add(region);
    }
    return Array.from(hits);
}

/* ---------- Data Dragon index + blurb-based region inference ---------- */
function useDDChampIndex() {
    const [ver, setVer] = useState(null);
    const [champs, setChamps] = useState([]); // [{id,name,tags,partype,range,blurb}]
    const [regionMap, setRegionMap] = useState({}); // { Region: [names] }

    useEffect(() => {
        let ok = true;
        (async () => {
            try {
                const vs = await fetch("https://ddragon.leagueoflegends.com/api/versions.json").then(r=>r.json());
                const v = vs?.[0]; if (!v || !ok) return;
                setVer(v);

                const idx = await fetch(`https://ddragon.leagueoflegends.com/cdn/${v}/data/en_US/champion.json`).then(r=>r.json());
                const arr = Object.values(idx?.data || {}).map(d => ({
                    id: d.id,
                    name: d.name,
                    blurb: d.blurb || "",
                    tags: (d.tags || []).map(t => t.toLowerCase()),
                    partype: (d.partype || "mana").toLowerCase(),
                    range: Number(d.stats?.attackrange ?? 125),
                }));
                if (!ok) return;
                setChamps(arr);

                // Build map from blurbs
                const map = {};
                arr.forEach(ch => {
                    const regions = guessRegionsFromText(`${ch.blurb} ${ch.name}`);
                    if (regions.length === 0) return;
                    regions.forEach(r => {
                        if (!map[r]) map[r] = [];
                        map[r].push(ch.name);
                    });
                });

                // Safety: ensure anchors exist for each region; if not, merge anchor + small static list
                for (const region of Object.keys(REGION_SYNONYMS)) {
                    const current = new Set(map[region] || []);
                    (REGION_ANCHORS[region] || []).forEach(n => current.add(n));
                    // if region detection is still weak, pad with a tiny static fallback
                    if (current.size < 6 && STATIC_REGION_FALLBACK[region]) {
                        STATIC_REGION_FALLBACK[region].forEach(n => current.add(n));
                    }
                    map[region] = Array.from(current).sort((a,b)=>a.localeCompare(b));
                }

                if (ok) setRegionMap(map);
            } catch (e) {
                console.error("DD index/regions failed", e);
                setRegionMap(STATIC_REGION_FALLBACK);
            }
        })();
        return () => { ok = false; };
    }, []);

    const icon = (cid) => ver ? `https://ddragon.leagueoflegends.com/cdn/${ver}/img/champion/${cid}.png` : null;
    return { ver, champs, icon, regionMap };
}

/* ---------- Riot: challenges config via proxy ---------- */
function useRiotChallenges() {
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);
    useEffect(() => {
        let ok = true;
        (async () => {
            try {
                setLoading(true);
                const r = await fetch(`${apiUrl}/api/riot/challenges/config`);
                const data = await r.json();
                if (!ok) return;
                setList(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("Challenges fetch failed", e);
            } finally {
                if (ok) setLoading(false);
            }
        })();
        return () => { ok = false; };
    }, []);
    return { loading, list };
}

/* ---------- rule engine ---------- */
function explainChallenge(c) {
    const name = c?.localizedNames?.en_US?.name ?? "Challenge";
    const desc = c?.localizedNames?.en_US?.description ?? "";
    const d = `${name} — ${desc}`.toLowerCase();

    const premade = /premade\s*5|\b5[-\s]?man\b/.test(d);
    const aram = /\baram\b/.test(d);
    const sr = /\bsummoner'?s rift\b|\bsr\b/.test(d);
    const factionMatch = d.match(/from the ([a-z\s&']+)/i);
    const faction = factionMatch ? factionMatch[1].replace(/\s+/g," ").replace(" & ", " & ").replace(/\bthe\b/gi,"").trim() : null;

    const steps = [];
    if (aram) steps.push("Queue ARAM; group early and draft poke/AoE.");
    if (sr) steps.push("Queue Summoner’s Rift (Normal Draft recommended for easier coordination).");
    if (premade) steps.push("Form a full 5-stack in a lobby before queueing to ensure progress counts.");
    if (faction) steps.push(`If it requires a faction, pick **five ${faction} champions**. See suggested comp below.`);
    if (/\bepic monster|baron|dragon|herald\b/.test(d)) steps.push("Secure early prio for objectives; swap bot to dragon at 8–10 min; bring Control Wards.");
    if (/\bassist\b/.test(d)) steps.push("Prioritize skirmishes; play champs with reliable CC to tag assists (support/jungle shine).");
    if (/\bvision|ward\b/.test(d)) steps.push("Buy early Support item/Control Wards; refresh wards on timers; sweep before fights.");
    if (/\btowers|turret\b/.test(d)) steps.push("Play split-push or siege champs; crash stacked waves and rotate for plates.");
    if (!steps.length) steps.push("Read the requirement carefully and play coordinated; draft to the condition.");
    return { premade, faction, steps };
}

/* ---------- text → filters ---------- */
function extractFiltersFromText(chal, regionKeys) {
    const t = `${chal?.localizedNames?.en_US?.name||""} ${chal?.localizedNames?.en_US?.description||""}`.toLowerCase();

    const regions = regionKeys.filter(r => t.includes(r.toLowerCase())) || [];
    if (/\byordle(s)?\b/.test(t) && !regions.includes("Bandle City")) regions.push("Bandle City");

    const classWords = ["assassin","fighter","mage","marksman","support","tank"];
    const classes = classWords.filter(c => new RegExp(`\\b${c}s?\\b`).test(t));

    const wantsRanged = /\branged\b/.test(t);
    const wantsMelee  = /\bmelee\b/.test(t);

    let resource = null;
    if (/\benergy\b/.test(t)) resource = "energy";
    else if (/\bmanaless|\bno\s+mana\b/.test(t)) resource = "none";
    else if (/\bfury|heat|grit|ferocity\b/.test(t)) resource = "fury";

    return { regions, classes, wantsRanged, wantsMelee, resource };
}

function filterEligibleChampions(filters, champs, regionMap) {
    const applied =
        (filters.regions?.length ? 1 : 0) +
        (filters.classes?.length ? 1 : 0) +
        (filters.wantsRanged || filters.wantsMelee ? 1 : 0) +
        (filters.resource ? 1 : 0);

    if (!applied) return [];

    let pool = champs;

    if (filters.regions?.length) {
        const allowedNames = new Set(filters.regions.flatMap(r => regionMap[r] || []));
        pool = pool.filter(c => allowedNames.has(c.name));
    }

    if (filters.classes?.length) {
        pool = pool.filter(c => c.tags.some(tag => filters.classes.includes(tag)));
    }

    if (filters.wantsRanged && !filters.wantsMelee) {
        pool = pool.filter(c => c.range >= 300);
    } else if (filters.wantsMelee && !filters.wantsRanged) {
        pool = pool.filter(c => c.range <= 200);
    }

    if (filters.resource) {
        const want = filters.resource;
        pool = pool.filter(c =>
            c.partype.includes(want) ||
            (want === "fury" && !["mana","energy"].includes(c.partype)) ||
            (want === "none" && (c.partype === "none" || c.partype === "blood well" || c.partype === "flow"))
        );
    }

    const seen = new Set();
    const res = pool.filter(c => (seen.has(c.id) ? false : (seen.add(c.id), true)))
        .sort((a,b)=>a.name.localeCompare(b.name));
    return res;
}

/* ---------- build a 5-man comp from a region pool ---------- */
function buildCompFromPool(pool, champs) {
    const dict = new Map(champs.map(c => [c.name, c]));
    const objs = pool.map(n => dict.get(n)).filter(Boolean);
    if (objs.length < 5) return null;

    const pickForLane = (lane, remaining) => {
        const sorted = [...remaining].sort((a,b) => {
            const ra = scoreRoles(a), rb = scoreRoles(b);
            const sa = 5 - ra.indexOf(lane);
            const sb = 5 - rb.indexOf(lane);
            return (sb - sa) || a.name.localeCompare(b.name);
        });
        return sorted[0] || null;
    };

    const chosen = {};
    let remaining = objs;
    for (const lane of ["top","jg","mid","adc","sup"]) {
        const pick = pickForLane(lane, remaining);
        if (!pick) return null;
        chosen[lane] = pick.name;
        remaining = remaining.filter(c => c.id !== pick.id);
    }
    return chosen;
}

/* ---------- UI ---------- */
export default function Challenges() {
    const { loading, list } = useRiotChallenges();
    const { champs, icon, regionMap } = useDDChampIndex();

    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);

    const filtered = useMemo(() => {
        const query = norm(q);
        return (list || [])
            .filter((c) => {
                const ln = c?.localizedNames?.en_US;
                const hay = `${ln?.name ?? ""} ${ln?.shortDescription ?? ""} ${ln?.description ?? ""}`.toLowerCase();
                return !query || hay.includes(query);
            })
            .sort((a,b) => (a.id - b.id));
    }, [list, q]);

    const detail = selected ? list.find((c) => c.id === selected) : filtered[0];
    const explanation = useMemo(() => detail ? explainChallenge(detail) : null, [detail]);

    const eligible = useMemo(() => {
        if (!detail || champs.length === 0) return [];
        const regionKeys = Object.keys(regionMap);
        const filters = extractFiltersFromText(detail, regionKeys);
        return filterEligibleChampions(filters, champs, regionMap);
    }, [detail, champs, regionMap]);

    const filtersSummary = useMemo(() => {
        if (!detail) return null;
        const regionKeys = Object.keys(regionMap);
        const f = extractFiltersFromText(detail, regionKeys);
        const bits = [];
        if (f.regions?.length) bits.push(`Regions: ${f.regions.join(", ")}`);
        if (f.classes?.length) bits.push(`Classes: ${f.classes.join(", ")}`);
        if (f.wantsRanged && !f.wantsMelee) bits.push("Ranged");
        if (f.wantsMelee && !f.wantsRanged) bits.push("Melee");
        if (f.resource) bits.push(`Resource: ${f.resource}`);
        return bits;
    }, [detail, regionMap]);

    const comp = useMemo(() => {
        const f = explanation?.faction;
        if (!f) return null;
        const pool = regionMap[f] || STATIC_REGION_FALLBACK[f] || [];
        return buildCompFromPool(pool, champs);
    }, [explanation, regionMap, champs]);

    return (
        <div className="min-h-screen text-slate-200" style={AURORA_BG}>
            <header className="sticky top-0 z-30 border-b border-white/10 backdrop-blur-md" style={{ background: "linear-gradient(to right, rgba(15,23,42,.65), rgba(15,23,42,.45))" }}>
                <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 flex items-center gap-3">
                    <div className="text-lg sm:text-xl font-extrabold tracking-tight">League Challenges <span className="opacity-60">— Aurora</span></div>
                    <div className="flex-1" />
                    <div className="relative w-72">
                        <input
                            value={q}
                            onChange={(e)=>setQ(e.target.value)}
                            placeholder="Search challenges…"
                            className="w-full bg-white/5 text-slate-100 border border-white/10 rounded-xl px-4 py-2 pr-9 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-cyan-400/30"
                        />
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rotate-90 text-slate-400 pointer-events-none" />
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-3 sm:px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* left: list */}
                <section className={`${CARD} p-3 lg:col-span-1`}>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">All Challenges {loading && <span className="opacity-60">…</span>}</div>
                    <div className="max-h-[70vh] overflow-auto space-y-2 pr-1">
                        {filtered.map((c) => {
                            const ln = c.localizedNames?.en_US || {};
                            const active = selected ? selected === c.id : filtered[0]?.id === c.id;
                            return (
                                <button
                                    key={c.id}
                                    onClick={()=>setSelected(c.id)}
                                    className={`w-full text-left p-3 rounded-xl border ${active ? "border-cyan-400/30 bg-cyan-400/10" : "border-white/10 bg-white/5 hover:bg-white/10"} `}
                                >
                                    <div className="text-sm font-semibold truncate">{ln.name || `#${c.id}`}</div>
                                    <div className="text-xs text-slate-400 line-clamp-2">{ln.shortDescription || ln.description || "—"}</div>
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* right: detail */}
                <section className="lg:col-span-2 space-y-6">
                    {detail && (() => {
                        const ln = detail.localizedNames?.en_US || {};
                        const tiers = Object.entries(detail.thresholds || {}).sort((a,b)=>a[1]-b[1]);
                        return (
                            <>
                                <div className={`${CARD} p-4`}>
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-bold text-slate-100">{ln.name || `Challenge #${detail.id}`}</div>
                                            <div className="text-slate-300 text-sm mt-1">{ln.description || ln.shortDescription || "No description"}</div>
                                        </div>
                                        <div className="text-xs text-slate-400">ID: {detail.id}</div>
                                    </div>
                                    {tiers.length > 0 && (
                                        <div className="mt-4">
                                            <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Tier thresholds</div>
                                            <div className="flex flex-wrap gap-2">
                                                {tiers.map(([tier, value]) => (
                                                    <div key={tier} className="px-3 py-1.5 rounded-xl border border-white/10 bg-white/5 text-xs">
                                                        {tier}: <span className="font-mono">{value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className={`${CARD} p-4`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Target className="w-4 h-4 text-emerald-300" />
                                        <h3 className="text-sm font-bold text-slate-100">How to complete</h3>
                                    </div>
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-300">
                                        {explanation?.steps?.map((s, i) => <li key={i}>{s}</li>)}
                                    </ul>
                                    {!!detail.tags?.length && (
                                        <div className="mt-3 text-xs text-slate-400 flex items-center gap-2">
                                            <Info className="w-3.5 h-3.5" />
                                            Tags: {detail.tags.join(", ")}
                                        </div>
                                    )}
                                </div>

                                {/* Eligible champions (inferred) */}
                                <div className={`${CARD} p-4`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Swords className="w-4 h-4 text-rose-300" />
                                        <h3 className="text-sm font-bold text-slate-100">Eligible Champions (inferred)</h3>
                                    </div>
                                    {filtersSummary?.length ? (
                                        <div className="text-xs text-slate-400 mb-3">
                                            {filtersSummary.join(" • ")}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-slate-300">
                                            No explicit champion restriction detected in this challenge. Play any champion and follow the tactics above.
                                        </div>
                                    )}
                                    {eligible.length > 0 && (
                                        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                                            {eligible.slice(0, 50).map((c) => (
                                                <div key={c.id} className="flex items-center gap-2 px-2.5 py-2 rounded-xl border border-white/10 bg-white/5">
                                                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                                                    {icon(c.id) && <img src={icon(c.id)} alt={c.name} className="w-6 h-6 rounded-md" />}
                                                    <div className="text-sm truncate">{c.name}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Premade 5 suggestion if factional */}
                                {explanation?.faction && (
                                    <div className={`${CARD} p-4`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <Users className="w-4 h-4 text-cyan-300" />
                                            <h3 className="text-sm font-bold text-slate-100">Suggested 5-man ({explanation.faction})</h3>
                                        </div>
                                        {(() => {
                                            const pool = regionMap[explanation.faction] || STATIC_REGION_FALLBACK[explanation.faction] || [];
                                            const chosen = buildCompFromPool(pool, champs);
                                            if (!chosen) return <div className="text-sm text-slate-300">Not enough champions detected for {explanation.faction}.</div>;
                                            return (
                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-sm">
                                                    {Object.entries(chosen).map(([lane, champ]) => (
                                                        <div key={lane} className="px-3 py-2 rounded-xl border border-white/10 bg-white/5">
                                                            <div className="text-xs uppercase tracking-wide text-slate-400">{lane}</div>
                                                            <div className="font-medium">{champ}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}
                                        <div className="mt-3 text-xs text-slate-400">
                                            Tip: swap lanes based on comfort; any champion from {explanation.faction} works if roles are covered.
                                        </div>
                                    </div>
                                )}

                                <div className={`${CARD} p-4`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Swords className="w-4 h-4 text-violet-300" />
                                        <h3 className="text-sm font-bold text-slate-100">Beginner tactics</h3>
                                    </div>
                                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-300">
                                        <li>Play to the condition first; winrate rises when you draft <em>for</em> the challenge.</li>
                                        <li>Communicate in lobby what you’re doing (role targets, timers, ward plans).</li>
                                        <li>Use Control Wards on every back; convert prio into objectives and safe fights.</li>
                                    </ul>
                                </div>
                            </>
                        );
                    })()}
                </section>
            </main>

            <footer className="max-w-6xl mx-auto px-3 sm:px-4 pb-8">
                <div className="text-xs text-slate-400/80">
                    <span className="inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Uses Riot lol-challenges-v1 (server-side) + Data Dragon blurbs + synonym/anchor inference.</span>
                </div>
            </footer>
        </div>
    );
}
